import { NSButton, NSCard } from 'bricks';
import React, { useState } from 'react';
import classNames from 'classnames';
import {
    faArrowLeftToLine, faArrowRight, faArrowLeft, faCaretDown, faLightbulbGear,
} from '@fortawesome/pro-solid-svg-icons';
import './ProFormaWizardStepper.scss';
import RenderIf from 'components/RenderIf/RenderIf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import BackOfEnvelopeKeyValues from 'components/BackOfEnvelopeKeyValues/BackOfEnvelopeKeyValues';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { INTEREST_RESERVE_WIZARD_TEXT, PRO_FORMA_WIZARD_STEPS } from 'views/ProFormaWizard/constants';
import { useProFormaWizard } from '../../context/ProFormaWizardProvider';

export interface IProFormaWizardStepProps {
    boeScenario?: BOEScenario;
}

const ProFormaWizardStepper = ({ boeScenario }: IProFormaWizardStepProps) => {
    const {
        proForma, saveAsDraft, currentStep, handleNextStep, handlePrevStep, isLoading,
    } = useProFormaWizard();

    const [showBackOfEnvelopeView, setShowBackOfEnvelopeView] = useState(false);

    const steps = ['Basic information', 'Development', 'Operations'];

    const handleViewOriginalScenario = () => {
        setShowBackOfEnvelopeView(!showBackOfEnvelopeView);
    };

    return (
        <NSCard className="ProFormaWizardStepper__card shadow">
            <div className="d-flex mx-2 justify-content-between align-items-center">
                <NSButton
                    className={classNames('ProFormaWizardStepper__backButton', {
                        'heap-deal-wizard-exit': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_1,
                        'heap-deal-wizard-step-2-back': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_2,
                        'heap-deal-wizard-step-3-back': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_3,
                    })}
                    icon={currentStep === 1 ? faArrowLeftToLine : faArrowLeft}
                    color="secondary"
                    text={currentStep === 1 ? 'Exit' : 'Back'}
                    callback={handlePrevStep}
                    disabled={isLoading}
                />

                <ol className="ProFormaWizardStepper__horizontalStepper">
                    {steps.map((step, index) => (
                        <li
                            key={step}
                            className={`ProFormaWizardStepper__horizontalStepper__item 
                                ${currentStep === index + 1 ? 'ProFormaWizardStepper__horizontalStepper__item--active' : ''}`}
                        >
                            {currentStep === index + 1 ? <div className="ProFormaWizardStepper__horizontalStepper__small--active" /> : null}
                            <p
                                className={`ProFormaWizardStepper__horizontalStepper__title ${currentStep === index + 1
                                    ? 'ProFormaWizardStepper__horizontalStepper__title--active' : ''
                                }`}
                            >
                                {step}
                            </p>
                        </li>
                    ))}
                </ol>

                <div className="d-flex justify-content-end">
                    <RenderIf isTrue={proForma?.boeScenario?.id && boeScenario}>
                        <div>
                            <Dropdown
                                isOpen={showBackOfEnvelopeView}
                                toggle={handleViewOriginalScenario}
                            >
                                <DropdownToggle
                                    className="d-flex align-items-center"
                                    color="secondary"
                                >
                                    <span>View original scenario</span>
                                    <FontAwesomeIcon icon={faCaretDown} className="ml-1" />
                                </DropdownToggle>
                                <DropdownMenu className="pb-3">
                                    <div className="p-3 overflow-auto">
                                        <BackOfEnvelopeKeyValues
                                            boeScenario={boeScenario}
                                            className="BackOfEnvelopeKeyValues__wizard-height"
                                        />
                                    </div>
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    </RenderIf>

                    <RenderIf isTrue={currentStep === PRO_FORMA_WIZARD_STEPS.STEP_2}>
                        <IconWithTooltip
                            name="interestReserveIconWizard-header"
                            icon={faLightbulbGear}
                            tooltipText={INTEREST_RESERVE_WIZARD_TEXT}
                            className="my-auto"
                        />
                    </RenderIf>

                    <NSButton
                        color="secondary"
                        text="Save draft"
                        className="mx-2 heap-deal-wizard-save-draft"
                        callback={saveAsDraft}
                        disabled={isLoading}
                        isLoading={isLoading}
                    />
                    <NSButton
                        className={classNames('ProFormaWizardStepper__nextButton text-nowrap', {
                            'heap-deal-wizard-step-1-next': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_1,
                            'heap-deal-wizard-step-2-next': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_2,
                            'heap-deal-wizard-finalize': currentStep === PRO_FORMA_WIZARD_STEPS.STEP_3,
                        })}
                        icon={faArrowRight}
                        iconPosition="right"
                        color="primary"
                        text={currentStep === 3 ? 'Finalize' : 'Next'}
                        callback={handleNextStep}
                        isLoading={isLoading}
                        disabled={isLoading}
                    />
                </div>
            </div>
        </NSCard>
    );
};

export default ProFormaWizardStepper;
